import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  HelpCircle, MapPin, AlertCircle, X, Sparkles, Tag,
  MessageSquare, PieChart, Maximize2, Tent, Network,
} from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { InlineTypeCreator, InlineEventCreator, InlineTriggerCreator } from './InlineCreatePopovers';
import GuidedLocationPicker from './GuidedLocationPicker';
import LocationSelector from './LocationSelector';
import MultiSelectDropdown from './MultiSelectDropdown';

const SEV_COLORS = {
  High: 'bg-red-100 text-red-700 border-red-200',
  Medium: 'bg-orange-100 text-orange-700 border-orange-200',
  Low: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};
const SEV_COLORS_DARK = {
  High: 'bg-red-500/15 text-red-300 border-red-500/30',
  Medium: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  Low: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
};
const SevBadge = ({ severity, dark = false }) => (
  <Badge className={`border ${(dark ? SEV_COLORS_DARK : SEV_COLORS)[severity] || (dark ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-gray-100 text-gray-600 border-gray-200')} text-[10px] font-bold`}>
    {severity || '—'}
  </Badge>
);

const EXERCISE_CLASSIFICATIONS = ['Tabletop', 'Partial', 'Full-scale', 'Field', 'Joint'];
const EXERCISE_ICONS = { Tabletop: MessageSquare, Partial: PieChart, 'Full-scale': Maximize2, Field: Tent, Joint: Network };

/* Premium card wrapper — dark (Scenario Builder AGENT) and light (Create
   Scenario modal) tones; plain mode renders children untouched. `sky` switches
   the light tone to the LOCATION & SCOPE blue palette. */
const SECTION_CARD_TONES = {
  dark: { background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(150,165,210,0.12)', eyebrow: '#8a92b0', hairline: 'linear-gradient(90deg, rgba(150,165,210,0.25), transparent)' },
  light: { background: 'linear-gradient(180deg,#fbfbff,#f7f6ff)', border: '1px solid #e5e4f8', eyebrow: '#475569', hairline: 'linear-gradient(90deg, #e2e6f2, transparent)' },
  lightSky: { background: 'linear-gradient(180deg,#f5fafe,#f0f7fd)', border: '1px solid #d8e9f9', eyebrow: '#475569', hairline: 'linear-gradient(90deg, #d8e9f9, transparent)' },
};

const SectionCard = ({ tone, sky, icon, title, right, children }) => {
  if (!tone) return <>{children}</>;
  const t = SECTION_CARD_TONES[tone === 'light' && sky ? 'lightSky' : tone] || SECTION_CARD_TONES.dark;
  return (
    <div style={{ background: t.background, border: t.border, borderRadius: 16, padding: 20, ...(tone === 'light' ? { boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset' } : {}) }}>
      <div className="mb-4 flex items-center gap-2">
        {icon}
        <span className="text-[11.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: t.eyebrow }}>{title}</span>
        <span className="h-px flex-1" style={{ background: t.hairline }} />
        {right}
      </div>
      {children}
    </div>
  );
};

const isUnknownLocation = (loc) => loc === 'unknown' || (Array.isArray(loc) && loc.includes('unknown'));

/**
 * Self-contained editor for an exercise's scenario: classification (with inline "New"),
 * severity (auto + override), guided location (+ Unknown), exercise classification and
 * impacted departments (location-synced primary/secondary + recommended).
 * Emits the current payload via onChange. Only classification/location changes should
 * rebuild the plan — the parent decides what to send.
 */
export const ExerciseScenarioEditor = ({
  incident,
  onChange,
  dark = false,
  premium = false, // light-premium variant (Create Scenario modal)
  showExerciseFields = true,
  lockIncidentDefinition = false,
}) => {
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [bundle, setBundle] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);

  const [typeId, setTypeId] = useState(incident?.type_id || '');
  const [eventId, setEventId] = useState(incident?.event_id || 'none');
  const [triggerId, setTriggerId] = useState(incident?.trigger_id || 'none');
  const [severity, setSeverity] = useState(
    incident?.severity_overridden && incident?.severity ? incident.severity : 'auto'
  );
  const [exerciseClassification, setExerciseClassification] = useState(incident?.exercise_classification || '');
  const [location, setLocation] = useState(
    incident?.is_unknown_location ? 'unknown' : (Array.isArray(incident?.location) ? incident.location[0] : incident?.location) || ''
  );
  const [impacted, setImpacted] = useState(incident?.impacted_departments || []);
  const [guidedSearch, setGuidedSearch] = useState({ active: false, selectedParentId: null, breadcrumb: [] });

  const manualImpactedRef = useRef(new Set(incident?.impacted_departments || []));

  // Initial data
  useEffect(() => {
    (async () => {
      try {
        const [t, d, l] = await Promise.all([
          CommanServices.incidentClassifications(),
          CommanServices.departments(),
          CommanServices.locationszoneswithlinks(),
        ]);
        if (t?.status) setIncidentTypes((t.data || []).map((x) => ({ ...x, id: x.id || x._id })));
        if (d?.status) setDepartments((d.data || []).map((x) => ({ ...x, id: x.id || x._id })));
        const ldata = l?.data ?? l;
        const list = Array.isArray(ldata) ? ldata : (ldata?.locations || ldata?.zones || []);
        setLocations(list);
      } catch (e) {
        console.error('scenario editor init failed', e);
      }
    })();
  }, []);

  // Bundle (events/triggers) for the chosen type
  useEffect(() => {
    if (!typeId) { setEvents([]); setTriggers([]); setBundle(null); return; }
    (async () => {
      try {
        const res = await CommanServices.getincidentclassificationtypes(typeId);
        if (res?.status) {
          const b = res.data;
          const ev = (b.events || []).map((e) => ({ ...e, id: e.id || e._id }));
          const tr = (b.triggers || []).map((x) => ({ ...x, id: x.id || x._id }));
          setEvents(ev); setTriggers(tr); setBundle({ ...b, events: ev, triggers: tr });
        }
      } catch (e) { console.error('bundle fetch failed', e); }
    })();
  }, [typeId]);

  const visibleTriggers = useMemo(
    () => (eventId === 'none' ? triggers : triggers.filter((t) => String(t.event_id) === String(eventId))),
    [triggers, eventId]
  );

  const autoSeverity = useMemo(() => {
    if (!bundle) return { severity: incident?.severity || '', from: 'Type' };
    if (triggerId && triggerId !== 'none') {
      const t = triggers.find((x) => x.id === triggerId && String(x.event_id) === String(eventId));
      if (t?.severity) return { severity: t.severity, from: 'Trigger' };
    }
    if (eventId && eventId !== 'none') {
      const e = events.find((x) => x.id === eventId);
      if (e?.severity) return { severity: e.severity, from: 'Event' };
    }
    return { severity: bundle?.type?.severity || incident?.severity || '', from: 'Type' };
  }, [bundle, triggers, events, typeId, eventId, triggerId, incident]);

  // Location → primary/secondary department map
  const locationDeptMap = useMemo(() => {
    const map = {};
    if (location && location !== 'unknown') {
      const loc = locations.find((l) => l.id === location);
      (loc?.departments || []).forEach((da) => {
        const did = da.department_id || da.id;
        if (did) map[did] = da.relationship_type || 'primary';
      });
    }
    return map;
  }, [location, locations]);

  // Sync impacted departments when location changes (auto-add primary/secondary, keep manual)
  useEffect(() => {
    const next = new Set(Array.from(manualImpactedRef.current));
    Object.keys(locationDeptMap).forEach((did) => next.add(did));
    setImpacted(Array.from(next));
  }, [locationDeptMap]);

  const deptName = (id) => {
    const d = departments.find((x) => String(x.id) === String(id));
    return d ? (d.department_name || d.name) : id;
  };

  const handleImpactedChange = (selected) => {
    manualImpactedRef.current = new Set(selected);
    setImpacted(selected);
  };

  const removeImpacted = (id) => {
    const next = impacted.filter((x) => String(x) !== String(id));
    manualImpactedRef.current = new Set(next);
    setImpacted(next);
  };

  // Emit payload
  useEffect(() => {
    if (!onChange) return;
    const sevToSave = severity === 'auto' ? autoSeverity.severity : severity;
    const typeName = incidentTypes.find((t) => String(t.id) === String(typeId))?.incident_type || '';
    const eventName = eventId === 'none' ? '' : (events.find((e) => String(e.id) === String(eventId))?.name || '');
    const triggerName = triggerId === 'none' ? '' : (triggers.find((t) => String(t.id) === String(triggerId))?.name || '');
    const locObj = locations.find((l) => String(l.id) === String(location));
    const locName = location === 'unknown'
      ? 'Unknown (Needs Mapping)'
      : (locObj?.auto_label || locObj?.label || locObj?.name || locObj?.display_label || '');
    onChange({
      type_id: typeId || '',
      event_id: eventId === 'none' ? '' : eventId,
      trigger_id: triggerId === 'none' ? '' : triggerId,
      incident_type: typeName,
      event: eventName,
      trigger: triggerName,
      severity: sevToSave,
      exercise_classification: exerciseClassification,
      location: location === 'unknown' ? [] : (location ? [location] : []),
      location_names: locName ? [locName] : [],
      is_unknown_location: location === 'unknown',
      impacted_departments: impacted,
    });
  }, [typeId, eventId, triggerId, severity, autoSeverity.severity, exerciseClassification, location, impacted, incidentTypes, events, triggers, locations]);

  const unknownSelected = isUnknownLocation(location);

  // Theming: `dark` = Scenario Builder AGENT violet; `premium` = light Create
  // Scenario modal; neither = the original plain look used everywhere else.
  const styled = dark || premium;
  const tone = dark ? 'dark' : (premium ? 'light' : null);
  const lbl = dark ? 'text-[13px] font-semibold text-[#cbd2e8]'
    : premium ? 'text-[13px] font-semibold text-[#334155]' : '';
  const trig = dark
    ? 'rounded-[11px] bg-[rgba(7,10,28,0.6)] border-[rgba(150,165,210,0.2)] text-white hover:border-[#a855f7]/50 focus:ring-2 focus:ring-[rgba(168,85,247,0.2)] focus:border-[#a855f7] data-[placeholder]:text-[#4a5378]'
    : premium
      ? 'rounded-[11px] bg-white border-[#dbe0f0] text-[#0f172a] hover:border-[#7c3aed]/50 focus:ring-2 focus:ring-[rgba(124,58,237,0.12)] focus:border-[#7c3aed] data-[placeholder]:text-[#a3abc4]'
      : '';
  const menu = dark
    ? 'z-[1300] no-scrollbar bg-[#0d1128] border-[rgba(168,133,255,0.2)] text-[#e6e9f6] [&_[role=option]]:text-[#e6e9f6] [&_[role=option]:focus]:bg-[rgba(168,85,247,0.16)] [&_[role=option]:focus]:text-white [&_[role=option][data-state=checked]]:bg-[rgba(168,85,247,0.12)]'
    : '';
  const sub = dark ? 'text-[#8a92b0]' : 'text-gray-500';
  // "+ New" inline-creator links: indigo in both premium tones.
  const newLink = dark ? '[&_button]:!text-[#818cf8] [&_button:hover]:!text-[#a5b4fc]'
    : premium ? '[&_button]:!text-[#4f46e5] [&_button:hover]:!text-[#6366f1]' : '';

  return (
    <div className={styled ? 'grid gap-[22px]' : 'space-y-4'} data-testid="exercise-scenario-editor">
      <SectionCard tone={tone} icon={<Tag className={`h-3.5 w-3.5 ${dark ? 'text-violet-400' : 'text-[#7c3aed]'}`} />} title="Incident Classification">
      {/* Classification */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className={`text-sm font-medium ${lbl}`}>Incident Type <span className={dark ? 'text-[#f87171]' : (premium ? 'text-[#dc2626]' : 'text-red-500')}>*</span></label>
            {!lockIncidentDefinition && <span className={newLink}><InlineTypeCreator onTypeCreated={async (nt) => {
              await CommanServices.incidentClassifications().then((t) => t?.status && setIncidentTypes((t.data || []).map((x) => ({ ...x, id: x.id || x._id }))));
              setTypeId(nt.id); setEventId('none'); setTriggerId('none');
              setSeverity('auto');
            }} /></span>}
          </div>
          <Select value={typeId} onValueChange={(v) => { setTypeId(v); setEventId('none'); setTriggerId('none'); }} disabled={lockIncidentDefinition}>
            <SelectTrigger data-testid="scenario-type-select" className={`text-sm ${trig} ${styled && !typeId ? (dark ? '!border-[rgba(248,113,113,0.45)]' : '!border-[#fca5a5]') : ''}`}><SelectValue placeholder="Select incident type" /></SelectTrigger>
            <SelectContent className={`max-h-72 overflow-y-auto ${menu}`}>
              {incidentTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.incident_type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!typeId && (
            <p className={`mt-1 flex items-center gap-1 text-xs ${dark ? 'text-[#f87171]' : (premium ? 'text-[#dc2626]' : 'text-red-500')}`}>
              {styled && <AlertCircle className="h-3 w-3" />} This field is required.
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className={`text-sm font-medium ${lbl}`}>Event</label>
            {!lockIncidentDefinition && <span className={newLink}><InlineEventCreator selectedTypeId={typeId} disabled={!typeId} onEventCreated={async () => {
              const res = await CommanServices.getincidentclassificationtypes(typeId);
              if (res?.status) setEvents((res.data.events || []).map((e) => ({ ...e, id: e.id || e._id })));
            }} /></span>}
          </div>
          <Select value={eventId} onValueChange={(v) => { setEventId(v); setTriggerId('none'); }} disabled={lockIncidentDefinition || !typeId}>
            <SelectTrigger data-testid="scenario-event-select" className={`text-sm ${trig}`}><SelectValue placeholder="Select event" /></SelectTrigger>
            <SelectContent className={`max-h-72 overflow-y-auto ${menu}`}>
              <SelectItem value="none">No specific event</SelectItem>
              {events.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className={`text-sm font-medium ${lbl}`}>Trigger</label>
            {!lockIncidentDefinition && <span className={newLink}><InlineTriggerCreator
              selectedTypeId={typeId}
              selectedEventName={eventId !== 'none' ? (events.find((e) => e.id === eventId)?.name || null) : null}
              disabled={!eventId || eventId === 'none'}
              onTriggerCreated={async () => {
                const res = await CommanServices.getincidentclassificationtypes(typeId);
                if (res?.status) setTriggers((res.data.triggers || []).map((x) => ({ ...x, id: x.id || x._id })));
              }}
            /></span>}
          </div>
          <Select value={triggerId} onValueChange={setTriggerId} disabled={lockIncidentDefinition || !eventId || eventId === 'none'}>
            <SelectTrigger data-testid="scenario-trigger-select" className={`text-sm ${trig}`}>
              <SelectValue placeholder={!eventId || eventId === 'none' ? 'Select event first' : 'Select trigger'} />
            </SelectTrigger>
            <SelectContent className={`max-h-72 overflow-y-auto ${menu}`}>
              <SelectItem value="none">No specific trigger</SelectItem>
              {visibleTriggers.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Severity */}
      <div className={styled ? 'mt-5 pt-4' : ''} style={styled ? { borderTop: dark ? '1px dashed rgba(150,165,210,0.15)' : '1px dashed #e2e6f2' } : undefined}>
        <label className={`block text-sm font-medium mb-2 ${lbl}`}>Incident Severity</label>
        <div className="flex items-center flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <SevBadge severity={autoSeverity.severity} dark={dark} />
            <span className={`text-xs ${sub}`}>(from {autoSeverity.from})</span>
          </div>
          <span className={dark ? 'text-[#4a5378]' : (premium ? 'text-[#e2e6f2]' : 'text-gray-300')}>|</span>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${sub}`}>Override:</span>
            <Select value={severity} onValueChange={setSeverity} disabled={lockIncidentDefinition}>
              <SelectTrigger data-testid="scenario-severity-select" className={`w-32 h-9 text-sm ${trig}`}><SelectValue placeholder="Use Auto" /></SelectTrigger>
              <SelectContent className={menu}>
                <SelectItem value="auto">Use Auto</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
            {severity !== 'auto' && <span className={`text-xs font-medium ${dark ? 'text-amber-300' : 'text-amber-600'}`}>Overridden</span>}
          </div>
        </div>
      </div>
      </SectionCard>

      <SectionCard
        tone={tone}
        sky
        icon={<MapPin className={`h-3.5 w-3.5 ${dark ? 'text-sky-400' : 'text-[#0284c7]'}`} />}
        title="Location & Scope"
        right={styled && (
          <button type="button" disabled={lockIncidentDefinition}
            className="flex items-center gap-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
            style={{ color: dark ? '#6ee7b7' : '#0284c7' }}
            onClick={() => setGuidedSearch({ active: true, selectedParentId: null, breadcrumb: [] })}>
            <HelpCircle className="h-3 w-3" /> Guided Location
          </button>
        )}
      >
      {/* Location */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className={`text-sm font-medium ${lbl}`}>Incident Location <span className={dark ? 'text-[#f87171]' : (premium ? 'text-[#dc2626]' : 'text-red-500')}>*</span></label>
          {styled ? (
            // premium modes: no "Unknown Location" hint; the badge only
            // appears if guided search already marked the location unknown.
            unknownSelected ? (
              <span className={`flex items-center gap-1 text-xs font-bold ${dark ? 'text-amber-400' : 'text-amber-600'}`}>
                <MapPin className="h-3 w-3" /> Location marked as Unknown
              </span>
            ) : null
          ) : (
            <button type="button" disabled={lockIncidentDefinition} className="text-xs font-medium flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50 text-indigo-600 hover:text-indigo-700"
              onClick={() => setGuidedSearch({ active: true, selectedParentId: null, breadcrumb: [] })}>
              <HelpCircle className="h-3 w-3" /> Guided Location
            </button>
          )}
        </div>
        <LocationSelector
          value={location === 'unknown' ? '' : location}
          onChange={(v) => setLocation(Array.isArray(v) ? v[0] : v)}
          mode="single"
          allowWildcard={false}
          placeholder="Select incident location..."
          className="w-full"
          dark={dark}
          disabled={lockIncidentDefinition}
        />
        <GuidedLocationPicker
          locations={locations}
          guidedSearch={guidedSearch}
          setGuidedSearch={setGuidedSearch}
          onApplyLocation={(sel) => {
            if (sel?.location_id) setLocation(sel.location_id);
            else if (sel?.location_match === 'Unknown (Needs Mapping)') setLocation('unknown');
            setGuidedSearch({ active: false, selectedParentId: null, breadcrumb: [] });
          }}
        />
        {!styled && (
          <div className="flex items-center justify-between mt-2">
            <button type="button"
              disabled={lockIncidentDefinition}
              className={`text-xs flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50 ${unknownSelected ? 'text-amber-600 font-bold' : 'text-slate-500 hover:text-slate-700'}`}
              onClick={() => setLocation(unknownSelected ? '' : 'unknown')}>
              <MapPin className="h-3 w-3" />
              <span>{unknownSelected ? 'Location marked as Unknown' : 'Unknown Location'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Impacted / Involved Departments */}
      <div className={dark ? 'mt-5' : ''}>
        {dark ? (
          <>
            <label className={`block text-sm font-medium mb-2 ${lbl}`}>Involved Organizations and Departments</label>
            <MultiSelectDropdown
              options={departments.map((d) => ({ value: d.id, label: d.department_name || d.name }))}
              selectedValues={impacted}
              onChange={handleImpactedChange}
              placeholder={departments.length === 0 ? 'No departments available' : 'Select departments involved...'}
              isSearchable
              hideSelectedChips
              dark={dark}
            />
            <p className="text-[12px] mt-2" style={{ color: '#5f6890' }}>Auto-filled from the selected location — adjust to refine the scenario match.</p>
            {impacted.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 mt-2.5">
                  <style>{`
                    @keyframes smartSheen { 0% { transform: translateX(-130%); } 55%, 100% { transform: translateX(130%); } }
                    .smart-rec-chip { position: relative; overflow: hidden; background-image: linear-gradient(110deg, #6366f1, #8b5cf6, #a855f7); }
                    .smart-rec-chip::after { content: ''; position: absolute; inset: 0; background: linear-gradient(75deg, transparent 42%, rgba(255,255,255,0.30) 50%, transparent 58%); transform: translateX(-130%); animation: smartSheen 6s ease-in-out infinite; pointer-events: none; }
                  `}</style>
                  <span className="smart-rec-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-sm">
                    <Sparkles className="h-3 w-3" /> Smartly recommended
                  </span>
                  <span className="text-[11px] font-semibold" style={{ color: '#8a92b0' }}>:</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="dept-role-list">
                  {[...impacted]
                    .sort((a, b) => {
                      const rank = (id) => { const r = locationDeptMap[String(id)]; return r === 'primary' ? 0 : (r === 'secondary' ? 1 : 2); };
                      return rank(a) - rank(b);
                    })
                    .map((deptId) => {
                      const role = locationDeptMap[String(deptId)];
                      const isPrimary = role === 'primary';
                      return (
                        <span key={deptId} data-testid={`dept-role-${deptId}`}
                          className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium"
                          style={{ background: 'rgba(7,10,28,0.6)', border: '1px solid rgba(150,165,210,0.2)', color: '#e6e9f6' }}>
                          {deptName(deptId)}
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide ${
                            isPrimary ? 'bg-emerald-500/20 text-emerald-300' : (role === 'secondary' ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700 text-slate-300')
                          }`}>
                            {role || 'manual'}
                          </span>
                          <button type="button" data-testid={`dept-role-remove-${deptId}`} onClick={() => removeImpacted(deptId)}
                            className="ml-0.5 text-[#8a92b0] hover:text-red-400 transition-colors" aria-label={`Remove ${deptName(deptId)}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <label className="block text-sm font-medium mb-2">Impacted Departments</label>
            <MultiSelectDropdown
              options={departments.map((d) => ({ value: d.id, label: d.department_name || d.name }))}
              selectedValues={impacted}
              onChange={handleImpactedChange}
              placeholder={departments.length === 0 ? 'No departments available' : 'Select departments...'}
              isSearchable
            />
            {impacted.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="impacted-departments-chips">
                {impacted.map((id) => {
                  const rel = locationDeptMap[id];
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] bg-gray-100 border border-gray-200">
                      <span className="font-medium text-gray-800">{deptName(id)}</span>
                      {rel === 'primary' && <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[9px] px-1.5 py-0">Primary</Badge>}
                      {rel === 'secondary' && <Badge className="bg-sky-100 text-sky-700 border border-sky-200 text-[9px] px-1.5 py-0">Secondary</Badge>}
                      {rel && <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-[9px] px-1.5 py-0">Recommended</Badge>}
                      <button type="button" className="text-gray-400 hover:text-red-500" onClick={() => removeImpacted(id)}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      </SectionCard>

      {/* Exercise Classification — exercises only; hidden when editing a real incident.
          Premium modes: segmented pills replacing the dropdown — same field value. */}
      {showExerciseFields && (styled ? (
        <div>
          <label className="mb-2.5 block text-[11.5px] font-bold uppercase" style={{ letterSpacing: '0.12em', color: dark ? '#8a92b0' : '#64748b' }}>
            Exercise Classification <span style={{ color: dark ? '#f87171' : '#dc2626' }}>*</span>
          </label>
          <div className="flex flex-wrap gap-2" data-testid="scenario-exercise-classification">
            {EXERCISE_CLASSIFICATIONS.map((c) => {
              const PillIcon = EXERCISE_ICONS[c] || MessageSquare;
              const active = exerciseClassification === c;
              const activeStyle = dark
                ? { background: 'linear-gradient(135deg,#a855f7,#d946ef)', color: '#fff',
                    boxShadow: '0 1px 0 rgba(255,255,255,0.2) inset, 0 6px 18px rgba(168,85,247,0.35)', border: '1px solid transparent' }
                : { background: 'linear-gradient(135deg,#4338ca,#7c3aed)', color: '#fff',
                    boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 18px rgba(79,70,229,0.35)', border: '1px solid transparent' };
              const idleStyle = dark
                ? { background: 'rgba(7,10,28,0.6)', color: '#8a92b0', border: '1px solid rgba(150,165,210,0.2)' }
                : { background: '#fff', color: '#475569', border: '1px solid #dbe0f0' };
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setExerciseClassification(c)}
                  data-testid={`scenario-exercise-pill-${c}`}
                  className="flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold transition-all"
                  style={active ? activeStyle : idleStyle}
                >
                  <PillIcon className="h-4 w-4" style={active ? undefined : { color: dark ? '#a78bfa' : '#7c3aed' }} />
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div>
          <label className={`block text-sm font-medium mb-2 ${lbl}`}>Exercise Classification <span className="text-red-500">*</span></label>
          <Select value={exerciseClassification} onValueChange={setExerciseClassification}>
            <SelectTrigger data-testid="scenario-exercise-classification" className={`text-sm ${trig}`}><SelectValue placeholder="Select exercise type" /></SelectTrigger>
            <SelectContent className={menu}>
              {EXERCISE_CLASSIFICATIONS.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
};

export default ExerciseScenarioEditor;
